import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Send, Heart } from 'lucide-react';
import { getComments, createComment, likeComment } from '../services/firestore';
import ShareButton from './ShareButton';

const DiscussModal = ({ isOpen, onClose, story, user }) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  // Load comments when modal opens or story changes
  useEffect(() => {
    if (isOpen && story?.id) {
      // Clear old comments immediately when story changes
      setComments([]);
      setComment('');

      // Load new comments using current story.id
      loadCommentsForStory(story.id);
    }
  }, [isOpen, story?.id]);

  const loadCommentsForStory = async (storyId) => {
    if (!storyId) return;
    setLoadingComments(true);
    try {
      const commentsData = await getComments(storyId);
      setComments(commentsData);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const loadComments = async () => {
    if (!story?.id) return;
    await loadCommentsForStory(story.id);
  };

  if (!isOpen || !story) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      alert('Please login to comment!');
      return;
    }

    if (!comment.trim()) {
      alert('Please enter a comment');
      return;
    }

    setIsSubmitting(true);

    try {
      const userName = user.displayName || user.email?.split('@')[0] || 'Anonymous';
      await createComment(story.id, 'story', user.uid, userName, comment);
      setComment('');
      await loadComments();
    } catch (error) {
      console.error('Failed to post comment:', error);
      alert('Failed to post comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!user) {
      alert('Please login to like comments!');
      return;
    }

    // Optimistic update
    setComments(prevComments =>
      prevComments.map(comment => {
        if (comment.id === commentId) {
          const hasLiked = comment.likers?.includes(user.uid);
          const newLikers = hasLiked
            ? (comment.likers || []).filter(id => id !== user.uid)
            : [...(comment.likers || []), user.uid];
          return {
            ...comment,
            likers: newLikers,
            likes: newLikers.length
          };
        }
        return comment;
      })
    );

    try {
      await likeComment(commentId, user.uid);
      // Reload to get accurate data and resort
      await loadComments();
    } catch (error) {
      console.error('Error liking comment:', error);
      alert('Failed to like comment. Please try again.');
      // Revert on error
      await loadComments();
    }
  };

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 md:p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[95vh] md:max-h-[90vh]">
        {/* Close Button - Always Visible */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 md:top-4 md:right-4 text-slate-400 hover:text-white transition z-50 bg-slate-900/80 backdrop-blur-sm rounded-full p-2"
        >
          <X className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        {/* Header */}
        <div className="p-4 md:p-6 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-slate-700 to-slate-600 flex items-center justify-center text-sm font-bold">
              {story.author?.[0] || '?'}
            </div>
            <div>
              <div className="font-bold text-white">{story.author}</div>
              <div className="text-xs text-slate-500">{timeAgo(story.createdAt)}</div>
            </div>
          </div>
          <h2 className="text-lg md:text-xl font-bold text-white mb-2 pr-8">
            {story.title}
          </h2>
          <p className="text-sm text-slate-400 leading-relaxed">
            {story.text}
          </p>
          <div className="mt-3">
            <ShareButton
              title={story.title}
              description={story.text}
              itemId={story.id}
              itemType="story"
              className="text-xs"
            />
          </div>
        </div>

        {/* Comments Section - Scrollable */}
        <div className="p-4 md:p-6 overflow-y-auto flex-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Discussion ({comments.length})
            </h3>
          </div>

          {/* Comments List */}
          <div className="space-y-4 mb-6">
            {loadingComments ? (
              <div className="text-center py-8 text-slate-500">
                <p>Loading comments...</p>
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No comments yet. Be the first to discuss!</p>
              </div>
            ) : (
              comments.map(comment => {
                const hasLiked = user && comment.likers?.includes(user.uid);
                return (
                  <div key={comment.id} className="bg-slate-950/50 rounded-lg p-4 border border-slate-800">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-amber-600 to-orange-500 flex items-center justify-center text-xs font-bold shrink-0">
                        {comment.userName?.[0] || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-white text-sm">{comment.userName}</span>
                          <span className="text-xs text-slate-500">{timeAgo(comment.createdAt)}</span>
                          <button
                            onClick={() => handleLikeComment(comment.id)}
                            className={`ml-auto flex items-center gap-1 text-xs transition ${
                              hasLiked ? 'text-red-500' : 'text-slate-400 hover:text-red-400'
                            }`}
                          >
                            <Heart className={`w-4 h-4 ${hasLiked ? 'fill-current' : ''}`} />
                            <span>{comment.likes || 0}</span>
                          </button>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed">
                          {comment.text}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

        </div>

        {/* Comment Form - Fixed at bottom */}
        <div className="border-t border-slate-800 p-4 md:p-6 shrink-0">
          {user ? (
            <form onSubmit={handleSubmit}>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-slate-700 to-slate-600 flex items-center justify-center text-xs font-bold shrink-0">
                  {user.displayName?.[0] || user.email?.[0] || 'U'}
                </div>
                <div className="flex-1">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Share your thoughts or similar experience..."
                    rows="3"
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 md:px-4 py-2 md:py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 transition resize-none text-sm"
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-slate-600">{comment.length}/1000</span>
                    <button
                      type="submit"
                      disabled={isSubmitting || !comment.trim()}
                      className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-3 md:px-4 py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                    >
                      <Send className="w-4 h-4" />
                      {isSubmitting ? 'Posting...' : 'Post'}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          ) : (
            <div className="text-center py-4 md:py-6 bg-slate-950/50 rounded-lg border border-slate-800">
              <p className="text-slate-400 mb-3 text-sm">Login to join the discussion</p>
              <button className="text-amber-500 hover:text-amber-400 font-bold underline text-sm">
                Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscussModal;
